/**
 * System Health dashboard view.
 *
 * Renders the operational health checks (jobs, config, data integrity,
 * capacity) grouped by category, with per-check status, drill-down detail,
 * and follow-up actions (acknowledge / resolve / re-open) so issues can be
 * owned and chased. Read model comes from /api/admin/system-health.
 */
import { escapeHtml, formatDateTimeFull } from '../../src/lib/format.js';
import * as api from '../api-client.js';
import * as notify from '../notifications.js';

const API = api.routes;
const showFlash = (msg, type) => notify.showFlash(msg, type);

let bodyEl, bannerEl, metaEl, refreshBtn;
let bound = false;
let lastSummary = null;

function resolveDom() {
  bodyEl = document.getElementById('sysHealthBody');
  bannerEl = document.getElementById('sysHealthBanner');
  metaEl = document.getElementById('sysHealthMeta');
  refreshBtn = document.getElementById('sysHealthRefreshBtn');
}

const STATUS_META = {
  ok: { icon: '✓', cls: 'sys-ok', label: 'OK' },
  warn: { icon: '!', cls: 'sys-warn', label: 'Warning' },
  fail: { icon: '✕', cls: 'sys-fail', label: 'Failing' },
  error: { icon: '✕', cls: 'sys-fail', label: 'Error' },
  unknown: { icon: '·', cls: 'sys-unknown', label: 'Not run' },
};

function statusBadge(status) {
  const m = STATUS_META[status] || STATUS_META.unknown;
  return `<span class="sys-badge ${m.cls}" title="${m.label}">${m.icon} ${escapeHtml(m.label)}</span>`;
}

function followupChip(fu) {
  if (!fu || fu.status === 'open') return '';
  const who = fu.assignee_username ? ` · ${escapeHtml(fu.assignee_username)}` : '';
  const label = fu.status === 'ack' ? 'Acknowledged' : fu.status === 'snoozed' ? 'Snoozed' : 'Resolved';
  return `<span class="sys-followup">${escapeHtml(label)}${who}</span>`;
}

function renderSummary(summary) {
  lastSummary = summary;
  if (!bodyEl) return;
  const counts = summary.counts || {};
  if (bannerEl) {
    const m = STATUS_META[summary.overall] || STATUS_META.unknown;
    bannerEl.className = `sys-health-banner ${m.cls}`;
    bannerEl.textContent = `${m.icon} ${counts.fail || 0} failing · ${counts.warn || 0} warnings · ${counts.unacked || 0} need follow-up`;
  }
  if (metaEl) {
    const ts = summary.generated_at ? formatDateTimeFull(summary.generated_at) : '';
    metaEl.textContent = ts ? `As of ${ts}` : '';
  }

  const cats = Array.isArray(summary.categories) ? summary.categories : [];
  bodyEl.innerHTML = cats.map((cat) => {
    const rows = (cat.checks || []).map((c) => {
      const detail = c.detail || {};
      const sample = Array.isArray(detail.sample) ? detail.sample : [];
      // "reason" explains the current state; "about" describes what the check
      // verifies. Keep them separate so a failing check doesn't show wording
      // that reads like it's healthy.
      const reason = detail.hint || detail.error || '';
      const about = c.description || '';
      const hasDetail = sample.length || reason || about;
      const firing = c.status === 'warn' || c.status === 'fail' || c.status === 'error';
      const actions = [`<button class="btn xs" data-sys-action="run" data-key="${escapeHtml(c.key)}">Re-run</button>`];
      if (firing) {
        if (!c.followup || c.followup.status === 'open') {
          actions.push(`<button class="btn xs" data-sys-action="ack" data-key="${escapeHtml(c.key)}">Acknowledge</button>`);
        }
        actions.push(`<button class="btn xs" data-sys-action="resolve" data-key="${escapeHtml(c.key)}">Resolve</button>`);
      } else if (c.followup && c.followup.status !== 'open') {
        actions.push(`<button class="btn xs" data-sys-action="reopen" data-key="${escapeHtml(c.key)}">Re-open</button>`);
      }
      const detailHtml = `
        <div class="sys-detail" data-detail-for="${escapeHtml(c.key)}" hidden>
          ${reason ? `<div class="table-meta">${firing ? '<strong>Reason:</strong> ' : ''}${escapeHtml(reason)}</div>` : ''}
          ${about ? `<div class="table-meta" style="opacity:.75;">What this checks: ${escapeHtml(about)}</div>` : ''}
          ${sample.length ? `<ul class="sys-sample">${sample.map((s) => `<li>${escapeHtml(String(s))}</li>`).join('')}</ul>` : ''}
          ${c.followup && c.followup.note ? `<div class="table-meta">Note: ${escapeHtml(c.followup.note)}</div>` : ''}
        </div>`;
      return `
        <tr class="${firing ? 'sys-row-firing' : ''}">
          <td>${statusBadge(c.status)}</td>
          <td>
            <strong>${escapeHtml(c.label)}</strong>
            ${followupChip(c.followup)}
            ${hasDetail ? `<button class="sys-link" data-sys-action="toggle" data-key="${escapeHtml(c.key)}">details</button>` : ''}
            ${detailHtml}
          </td>
          <td class="sys-value">${escapeHtml(c.value || '—')}</td>
          <td class="sys-actions">${actions.join(' ')}</td>
        </tr>`;
    }).join('');
    const m = STATUS_META[cat.rollup] || STATUS_META.unknown;
    return `
      <div class="card sys-cat">
        <h3 class="sys-cat-title">${statusBadge(cat.rollup)} ${escapeHtml(cat.name)}</h3>
        <div class="table-wrap">
          <table class="sys-table">
            <thead><tr><th style="width:120px;">Status</th><th>Check</th><th style="width:220px;">Measure</th><th style="width:240px;">Follow-up</th></tr></thead>
            <tbody>${rows}</tbody>
          </table>
        </div>
      </div>`;
  }).join('');
}

async function load(options = {}) {
  resolveDom();
  bindOnce();
  if (!bodyEl) return;
  if (bannerEl && !options.silent) bannerEl.textContent = 'Loading…';
  try {
    const summary = await api.fetch(API.adminSystemHealthSummary(!!options.run), { method: 'GET' });
    renderSummary(summary);
  } catch (err) {
    if (bodyEl) bodyEl.innerHTML = `<div class="table-meta">${escapeHtml(err && err.message ? err.message : 'Failed to load system health.')}</div>`;
  }
  loadJobs();
}

// ---- Background jobs roll-up + log harvesting ---------------------------

const JOB_HEALTH_META = {
  ok: { icon: '✓', cls: 'sys-ok' },
  warn: { icon: '!', cls: 'sys-warn' },
  fail: { icon: '✕', cls: 'sys-fail' },
  unknown: { icon: '·', cls: 'sys-unknown' },
};

function jobKindLabel(kind) {
  if (kind === 'timer') return 'Scheduled';
  if (kind === 'daemon') return 'Daemon';
  if (kind === 'on_demand') return 'On-demand';
  return kind || '';
}

function humanizeInterval(sec) {
  sec = Number(sec) || 0;
  if (sec % 86400 === 0) return `${sec / 86400}d`;
  if (sec % 3600 === 0) return `${sec / 3600}h`;
  if (sec % 60 === 0) return `${sec / 60}m`;
  return `${sec}s`;
}

function jobStatusCell(job) {
  const m = JOB_HEALTH_META[job.health] || JOB_HEALTH_META.unknown;
  const flags = [];
  if (job.hung) flags.push('<span class="sys-badge sys-fail" title="Running past its expected runtime">HUNG</span>');
  if (job.overdue) flags.push('<span class="sys-badge sys-warn" title="Missed its scheduled cadence">OVERDUE</span>');
  if (job.running && !job.hung) flags.push('<span class="sys-badge sys-unknown" title="Currently running">running</span>');
  const last = job.last_status ? escapeHtml(job.last_status) : '—';
  return `<span class="sys-badge ${m.cls}">${m.icon} ${last}</span> ${flags.join(' ')}`;
}

function renderJobs(payload) {
  const wrap = document.getElementById('sysHealthJobs');
  if (!wrap) return;
  const jobs = Array.isArray(payload.jobs) ? payload.jobs : [];
  if (!jobs.length) {
    wrap.innerHTML = '<div class="table-meta">No jobs recorded yet.</div>';
    return;
  }
  const rows = jobs.map((j) => {
    const last = j.last_started_at ? formatDateTimeFull(j.last_started_at) : 'never';
    const age = j.last_age_human ? ` (${escapeHtml(j.last_age_human)} ago)` : '';
    const cadence = j.interval_seconds ? `every ${humanizeInterval(j.interval_seconds)}` : (j.kind === 'on_demand' ? 'on demand' : 'continuous');
    const logsBtn = j.has_logs
      ? `<button class="btn xs" data-job-action="logs" data-job="${escapeHtml(j.name)}" data-label="${escapeHtml(j.label)}">Logs</button>`
      : `<button class="btn xs" data-job-action="logs" data-job="${escapeHtml(j.name)}" data-label="${escapeHtml(j.label)}" title="No dedicated unit — shows the last run record">Last run</button>`;
    const rowCls = j.health === 'fail' ? 'sys-row-firing' : '';
    const msg = j.last_message ? `<div class="table-meta" style="max-width:340px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;" title="${escapeHtml(j.last_message)}">${escapeHtml(j.last_message)}</div>` : '';
    return `
      <tr class="${rowCls}">
        <td><strong>${escapeHtml(j.label)}</strong><div class="table-meta">${escapeHtml(jobKindLabel(j.kind))} · ${escapeHtml(cadence)}</div></td>
        <td>${jobStatusCell(j)}</td>
        <td class="sys-value">${escapeHtml(last)}${age}${msg}</td>
        <td class="sys-actions">${logsBtn}</td>
      </tr>`;
  }).join('');
  wrap.innerHTML = `
    <div class="table-wrap">
      <table class="sys-table">
        <thead><tr><th>Job</th><th style="width:220px;">Status</th><th style="width:300px;">Last run</th><th style="width:110px;"></th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
    </div>`;
}

async function loadJobs() {
  const wrap = document.getElementById('sysHealthJobs');
  if (!wrap) return;
  try {
    const payload = await api.fetch(API.adminSystemHealthJobs, { method: 'GET' });
    renderJobs(payload);
  } catch (err) {
    wrap.innerHTML = `<div class="table-meta">${escapeHtml(err && err.message ? err.message : 'Failed to load jobs.')}</div>`;
  }
}

let currentLogJob = null;

async function openJobLogs(name, label) {
  currentLogJob = name;
  const dlg = document.getElementById('sysJobLogsDlg');
  const title = document.getElementById('sysJobLogsTitle');
  const out = document.getElementById('sysJobLogsOutput');
  const meta = document.getElementById('sysJobLogsMeta');
  if (!dlg) return;
  if (title) title.textContent = `Logs · ${label || name}`;
  if (out) out.textContent = 'Loading…';
  if (meta) meta.textContent = '';
  if (typeof dlg.showModal === 'function') { try { dlg.showModal(); } catch { /* already open */ } }
  await fetchJobLogs(name);
}

async function fetchJobLogs(name) {
  const out = document.getElementById('sysJobLogsOutput');
  const meta = document.getElementById('sysJobLogsMeta');
  try {
    const res = await api.fetch(API.adminSystemHealthJobLogs(name, 300), { method: 'GET' });
    const bits = [];
    if (res.unit) bits.push(`unit: ${res.unit}`);
    const st = res.state || {};
    if (st.ActiveState) bits.push(`state: ${st.ActiveState}/${st.SubState || ''}`);
    if (st.Result && st.Result !== 'success') bits.push(`result: ${st.Result}`);
    if (st.NRestarts && st.NRestarts !== '0') bits.push(`restarts: ${st.NRestarts}`);
    if (res.last_run) bits.push(`last run: ${res.last_run.status}${res.last_run.finished_at ? '' : ' (unfinished)'}`);
    if (meta) meta.textContent = bits.join(' · ');
    if (!out) return;
    if (Array.isArray(res.lines) && res.lines.length) {
      out.textContent = res.lines.join('\n');
      out.scrollTop = out.scrollHeight;
    } else if (res.available === false) {
      const parts = [];
      if (res.note) parts.push(res.note);
      if (res.error) parts.push(`Error: ${res.error}`);
      if (res.hint) parts.push(res.hint);
      if (res.last_run && res.last_run.message) parts.push(`\nLast run message:\n${res.last_run.message}`);
      out.textContent = parts.join('\n\n') || 'No logs available.';
    } else {
      out.textContent = 'No log lines returned.';
    }
  } catch (err) {
    if (out) out.textContent = (err && err.message) ? err.message : 'Failed to load logs.';
  }
}

async function runCheck(key) {
  try {
    const summary = await api.fetch(API.adminSystemHealthRun(key), { method: 'POST' });
    renderSummary(summary);
  } catch (err) {
    showFlash(err && err.message ? err.message : 'Re-run failed.', 'error');
  }
}

async function setFollowup(key, status) {
  let note = null;
  if (status === 'ack' || status === 'resolve') {
    note = window.prompt(status === 'ack' ? 'Acknowledge — add a note (who/what is being done):' : 'Resolve — add a note (optional):', '');
    if (note === null && status === 'ack') return; // cancelled
  }
  const apiStatus = status === 'resolve' ? 'resolved' : status === 'reopen' ? 'open' : status;
  try {
    const summary = await api.fetch(API.adminSystemHealthFollowups, {
      method: 'POST',
      body: JSON.stringify({ check_key: key, status: apiStatus, note: note || null }),
    });
    renderSummary(summary);
  } catch (err) {
    showFlash(err && err.message ? err.message : 'Update failed.', 'error');
  }
}

function bindOnce() {
  if (bound) return;
  bound = true;
  if (refreshBtn) refreshBtn.addEventListener('click', () => load({ run: true }));
  if (bodyEl) {
    bodyEl.addEventListener('click', (event) => {
      const btn = event.target.closest('[data-sys-action]');
      if (!btn) return;
      const action = btn.dataset.sysAction;
      const key = btn.dataset.key;
      if (action === 'toggle') {
        const d = bodyEl.querySelector(`.sys-detail[data-detail-for="${CSS.escape(key)}"]`);
        if (d) d.hidden = !d.hidden;
        return;
      }
      if (action === 'run') return void runCheck(key);
      if (['ack', 'resolve', 'reopen'].includes(action)) return void setFollowup(key, action);
    });
  }

  const jobsWrap = document.getElementById('sysHealthJobs');
  if (jobsWrap) {
    jobsWrap.addEventListener('click', (event) => {
      const btn = event.target.closest('[data-job-action="logs"]');
      if (!btn) return;
      openJobLogs(btn.dataset.job, btn.dataset.label);
    });
  }
  const jobsRefreshBtn = document.getElementById('sysJobsRefreshBtn');
  if (jobsRefreshBtn) jobsRefreshBtn.addEventListener('click', () => loadJobs());
  const logsReloadBtn = document.getElementById('sysJobLogsReloadBtn');
  if (logsReloadBtn) logsReloadBtn.addEventListener('click', () => { if (currentLogJob) fetchJobLogs(currentLogJob); });
}

export default { load };
